import { Column } from '@orion-platform/datagrid';
// Deep import: the barrel pulls the grid containers into the initial chunk.
import { resolveCaption } from 'libs/datagrid/src/lib/utils/column-captions';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { UserSettings } from '../models/supply-visibility.model';
import { buildAllowedSet, buildFallbackColumns } from '../store/slots/slots.utils';
import { ColumnsState, DefaultColumnsSource, ResolveUnknownColumn } from '../types/columns.types';

/** The column a grid draws first and the reader is not allowed to move. */
export const ACTIONS_FIELD = 'actions';

/**
 * The grid's selection checkbox; added by `Helper.setCheckColumn`, not by the
 * field structure. It sits ahead of every column the reader arranges.
 */
export const SELECTION_FIELD = 'isChecked';

/**
 * Whether a column is the actions column, whatever case it was written in.
 * @param {string | undefined} field The column's field name.
 * @returns {boolean} True for the actions column.
 */
export const isActionsField = (field?: null | string): boolean =>
  field?.toLowerCase() === ACTIONS_FIELD;

/**
 * Moves the actions column to the front, leaving every other column in the
 * order it was already in. A list without one is handed back untouched.
 * @param {T[]} columns The columns as they stand.
 * @param {number} from Index the actions column belongs at.
 * @returns {T[]} The same columns, actions first.
 */
export const actionsFirst = <T extends { field: string }>(columns: T[], from = 0): T[] => {
  const at = columns.findIndex((column): boolean => isActionsField(column.field));
  if (at < 0 || at === from) return columns;

  const rest = columns.filter((_, index): boolean => index !== at);
  rest.splice(from, 0, columns[at]);

  return rest;
};

/** The stored arrangement, or nothing where the record cannot be read as one. */
const parseStored = (data: string): Column[] | null => {
  try {
    const parsed: unknown = JSON.parse(data);

    return Array.isArray(parsed) ? (parsed as Column[]) : null;
  } catch {
    return null;
  }
};

/** Whether a failed lookup was the server saying there is nothing stored. */
const isNotFound = (error: unknown): boolean =>
  (error as null | { status?: number })?.status === 404;

/** Drops repeated fields — DevExtreme throws E1059 and draws nothing. */
const dedupeByField = (columns: Column[]): Column[] => {
  const seen = new Set<string>();
  return columns.filter((column): boolean => {
    const field = column.field as string | undefined;
    if (!field || seen.has(field)) return false;
    seen.add(field);
    return true;
  });
};

/**
 * Reconciles user-stored column definitions with the current default schema.
 *
 * Use this when the allowed default columns might have changed since the user
 * saved their settings. The function removes columns not in the default and
 * appends any default columns the user is missing.
 *
 * @param {Column[]} defaultCols - The current source-of-truth column definitions.
 * @param {Column[]} apiCols - The user-stored column definitions from the API.
 * @returns {Column[]} Columns limited to the default set, with any missing defaults appended.
 */
export const reconcileColumns = (
  schema: Column[],
  stored: Column[],
  resolveUnknown?: ResolveUnknownColumn,
): Column[] => {
  const known = new Map(schema.map((column): [string, Column] => [String(column.field), column]));

  // The user's order, and their visibility over the schema's own description.
  const kept = stored.flatMap((column): Column[] => {
    const base = known.get(String(column.field)) ?? resolveUnknown?.(column);
    return base ? [{ ...base, visible: column.visible } as Column] : [];
  });

  const arranged = new Set(stored.map((column): string => String(column.field)));

  // Columns added to the schema since the record was written.
  return kept.concat(schema.filter((column): boolean => !arranged.has(String(column.field))));
};

/**
 * Defines a stored column from the unfiltered structure, for an open-schema page.
 * @param {Column[]} catalogue The unfiltered field structure.
 * @returns {ResolveUnknownColumn} The column, captioned as well as it can be.
 */
export const fromCatalogue =
  (catalogue: Column[]): ResolveUnknownColumn =>
  (stored: Column): Column => {
    // Catalogue first: a caption stored before a rename would be the stale one.
    const entry = catalogue.find(
      (column): boolean =>
        String(column.field).toLowerCase() === String(stored.field).toLowerCase(),
    );

    return {
      ...stored,
      headerText: entry?.headerText ?? stored.headerText ?? String(stored.field),
      type: entry?.type ?? stored.type ?? 'string',
    } as Column;
  };

/**
 * A page's columns: its schema narrowed by the form settings, with the user's
 * saved arrangement merged over it. Any failure falls back to the defaults
 * rather than leaving the grid empty.
 */
export const loadDefaultColumns = ({
  buildDefaults,
  catalogue,
  columnListKey,
  formFields$,
  narrowToAllowed = false,
  persistDefaults,
  userSettings,
}: DefaultColumnsSource): Observable<ColumnsState> =>
  formFields$.pipe(
    // A plant with no form configuration is not an error: the schema is then
    // the whole list, which is what the pages did before this. Letting it
    // through leaves the grid or form spinning on columns that never arrive.
    catchError((): Observable<string[]> => of([])),
    switchMap((formFields: string[]): Observable<ColumnsState> => {
      const defaults = buildDefaults(formFields);
      const allowed = buildAllowedSet(formFields);
      const schema = narrowToAllowed
        ? defaults.filter((column): boolean => allowed.has(String(column.field).toLowerCase()))
        : defaults;

      const settle = (columns: Column[], defaultColumns: boolean): ColumnsState => ({
        columns: dedupeByField(columns),
        defaultColumns,
        defaultFields: formFields,
      });
      const fallback = (): ColumnsState => settle(buildFallbackColumns(schema, formFields), true);

      return userSettings(columnListKey).pipe(
        map((settings: UserSettings): ColumnsState => {
          // Two different questions. Which columns to draw depends on whether
          // an arrangement was stored; whether a save creates or edits depends
          // on whether a record came back at all. A record with no columns in
          // it still has to be edited — creating a second under the same key
          // is an error.
          const exists = Boolean(settings?.key || settings?.data);
          const stored = settings?.data ? parseStored(settings.data) : null;

          // Unreadable is not the same as absent: the record is still there, so
          // a save has to edit it. The schema is drawn meanwhile.
          if (!stored) return { ...fallback(), defaultColumns: !exists };

          return settle(
            reconcileColumns(schema, stored, catalogue && fromCatalogue(catalogue())),
            false,
          );
        }),
        catchError((error: unknown): Observable<ColumnsState> => {
          const defaults = fallback();

          // Only a 404 means "no entry yet". Writing one makes the user's first
          // change an edit rather than a create — and `defaultColumns` has to
          // say so, or that change posts a second record under the same key.
          if (isNotFound(error) && persistDefaults) {
            persistDefaults(columnListKey, defaults.columns).subscribe();

            return of({ ...defaults, defaultColumns: false });
          }

          return of(defaults);
        }),
      );
    }),
  );

/**
 * Puts back the captions the columns arrived with, so what is stored reads the
 * same to everyone whatever language it was arranged in.
 * @param {Column[]} updated The columns as the user left them.
 * @param {Column[]} original The columns as they were handed to the dialog.
 * @returns {Column[]} The user's arrangement, captioned as it came.
 */
export const restoreCaptions = (updated: Column[], original: Column[]): Column[] => {
  const captions = new Map<string, string>(
    original.map((column): [string, string] => [String(column.field), column.headerText]),
  );

  return updated.map((column): Column => ({
    ...column,
    headerText: captions.get(String(column.field)) ?? column.headerText,
  }));
};

/**
 * Captions a column list in the user's language; an untranslated field keeps the
 * caption it came with rather than showing a key.
 * @param {Column[]} columns The columns as the store holds them.
 * @param {string} prefix Namespace the captions are keyed under.
 * @param {(key: string) => string} translate Reads one key; `TranslateService.instant`.
 * @returns {Column[]} The same columns, captioned.
 */
export const translateColumns = (
  columns: Column[],
  prefix: string,
  translate: (key: string) => string,
): Column[] =>
  columns.map((column): Column => ({
    ...column,
    headerText: resolveCaption(column, prefix, translate),
  }));
